/*
	"Buy More & Save" — the matching-pieces upsell above the PDP's add to cart.

	The rows come from templates/product.buy-more-save-more.liquid, fetched in one
	request off the product being viewed: one row per collection on its
	`product_type_data` entry, each opened on the piece in this product's colour.
	Which pieces those are is curated per product type, so the rows arrive as the
	list to show — nothing is dropped from them here. Every row arrives complete —
	image, price, variant selector, quantity — so expanding a row reveals markup
	that is already in the page: showing and hiding it is done in CSS
	(product-buy-more.css).

	This element fetches that markup once, keeps each row's price and image in
	step with its variant selector, adds a row to the cart when its own Add is
	pressed, and moves the discount bar along as the cart fills.

	Every row is shopped on its own (WEB-3220). Ticking one is only opening it —
	the checkbox reveals the piece's selectors and its price, and nothing about
	it is added, counted or promised until its Add button is pressed (WEB-3221). That button
	puts that one piece in the cart immediately and by itself; the PDP's own add
	to cart buys the product being viewed and nothing else. The row then stays as
	it is, open, rather than tidying itself away, and its button reads "Added!"
	for a couple of seconds before it's back to Add, offering another; closing it
	before then puts it back to rest (WEB-3253). "Add another" clones
	a whole row, so a second size of the same piece is a second row with its own
	Add rather than a second block inside one — offered on pillowcases only.

	What's already in the cart counts towards the tier: pieces from the discount's
	own collection that the shopper picked up earlier carry it along with them, so
	a cart holding two of them prices this product at the tier three pieces
	reaches. The product's own price follows the cart as it fills, so a row
	added moves it on to the tier it would now reach (WEB-3267) — until the
	product itself goes in, through the PDP's add to cart or the sticky bar's.
	From then it holds where it was, so the piece just added isn't still offered
	a discount for being added (WEB-3223). Only a change made in the cart itself
	— a line's quantity stepped up or down, or removed — moves it after that,
	since that is the shopper changing what they already have (WEB-3256).
	Opening another product starts over, and a colour swap is another product:
	the swapped-in section brings a new widget that starts from the cart as it
	stands. The rows' own prices follow the live cart to the tier the next piece
	would reach (WEB-3234).

	The bar is the tiers themselves — "2 pieces, 3 pieces, 4 pieces, 5+ pieces"
	— filled to what the cart holds now. That count is live: it is re-read
	whenever the cart changes, this widget's adds, the PDP's and the cart
	drawer's alike, so the bar moves as pieces go in and come out. Ticking a row
	adds nothing, so it leaves the bar alone.
*/
/* The storefront's money format, with the trailing zeroes wrapped the way the
   price snippets wrap them so the hide-trailing-zeroes test still bites — and a
   short form for prices the row renders with money_without_trailing_zeros */
const money = cents => theme.Currency.formatMoney(cents).replace('.00', '<span class="zeroes">.00</span>');
const moneyShort = cents => theme.Currency.formatMoney(cents).replace('.00', '');

/* What the tier price is conditional on, said beside it. The widget prices this
   product at the tier its own add to cart would reach, so the figure under the
   title is a price the shopper only pays by adding it — the sticky bar and the
   size dropdown quote the same figure with no room to say so, so only the
   lockup under the title carries it. */
const TIER_PRICE_SUFFIX = 'when you add this to your order';

/* How long a row's button reads "Added!" before it's back to Add — the same as
   the theme's other add buttons (quick-add-to-cart.js, recommended-products.js). */
const ADDED_DURATION = 2000;

/* A variant's price in cents, honouring an active price test — same choice
   VariantSelector.firePriceChangedEvent makes. */
const variantPrice = variant => variant.priceTestPrices && window.getActivePriceTest()
	? parseInt(JSON.parse(variant.priceTestPrices)[window.getActivePriceTestIndex()])
	: parseInt(variant.price);

/* Ids and radio names are only unique per rendered row, so a cloned row has to
   be re-suffixed — its checkbox would otherwise share an id with the
   original's, so its label would tick the original instead, and its size
   radios would share a group, so picking one would unpick the other.

   Only radio names are rewritten. The other named elements in a selector —
   button[name="select-option"], button[name="size-guide"] — are addressed by
   name in CSS and by OptionSelector, so renaming those loses the dropdown's
   grid placement and its click handler. */
let cloneCount = 0;

const uniquify = item => {
	const suffix = `copy${++cloneCount}`;

	for (const el of item.querySelectorAll('[id], [for]')) {
		for (const attr of ['id', 'for']) {
			if (el.hasAttribute(attr)) el.setAttribute(attr, `${el.getAttribute(attr)}-${suffix}`);
		}
	}

	for (const radio of item.querySelectorAll('input[type="radio"][name]')) {
		radio.name = `${radio.name}-${suffix}`;
	}

	// The uid a colour swap re-applies to the selector it fetches, so a clone
	// keeps its own radio group after being swapped to another colour too.
	const selector = item.querySelector('variant-selector');
	if (selector) selector.dataset.uid = `${selector.dataset.uid}-${suffix}`;

	return item;
};

class BuyMoreSaveMore extends HTMLElement {
	constructor() {
		super();

		// Rows opened on this page so far, carried on every "Opened" event so
		// FullStory can tell how many a shopper opens per PDP (WEB-3246)
		this.openedCount = 0;

		// Percent off at the tier one more piece reaches from basePieces — what
		// the product being viewed is priced at. updateProgress() owns it.
		this.discount = 0;

		// The same, but from the cart as it stands rather than as the page
		// opened — what the rows' own prices are quoted at (WEB-3234).
		this.rowDiscount = 0;

		/* Pieces from this collection the cart is holding, in two readings:

		   - basePieces is what the product's own price is quoted against. It
		     follows the cart until the product itself is added (productAdded),
		     after which only a change made in the cart moves it (see readCart).
		     Seeded by the section so the prices are right in the first paint,
		     and corrected by the load-time cart read, which is what a cached
		     page needs;
		   - cartPieces is what it holds now. It starts as the same number and
		     moves with every cart change, which is what fills the bar and
		     prices the rows.

		   Both are per element: a colour swap replaces the section, and the new
		   one's cart-pieces is the cart as it stands after whatever went in
		   since — so the swapped-in colour, a piece not yet in the cart, is
		   quoted the tier adding it would reach (WEB-3223). */
		this.basePieces = parseInt(this.getAttribute('cart-pieces')) || 0;
		this.cartPieces = this.basePieces;

		/* Set once the product being viewed goes in through the PDP's add to
		   cart — the sticky bar's included, since it presses the same one. From
		   then an add no longer moves its price (WEB-3267). Announced before
		   the line is sent, so it's already set when the add lands. */
		this.productAdded = false;
		this.onProductAdd = () => { this.productAdded = true; };

		/* The ladder the discount is paid on, as the section states it —
		   "pieces:discount" in pieces the cart would end up holding, ascending. */
		this.tiers = (this.getAttribute('tiers') || '').split(',').map(tier => {
			const [pieces, discount] = tier.split(':');
			return { pieces: parseInt(pieces), discount: parseFloat(discount) };
		}).filter(tier => tier.pieces > 0);

		// The product form's sizes as of the last time they were pushed onto the
		// rows. Seeded properly in connectedCallback, off the form; empty here so
		// syncSizes() has something to compare against whenever it runs.
		this.syncedSizes = {};

		this.addEventListener('variantchange', e => this.onVariantChange(e));
		this.addEventListener('click', e => this.onClick(e));
		this.addEventListener('change', e => this.onToggle(e));

		// The PDP's selector is outside this element, so its variant changes only
		// reach us through the document-level announcement: a size change there
		// moves the price we re-quote at the tier, and carries onto the rows.
		//
		// Fires for the rows' own selectors too, since they announce themselves
		// the same way. syncSizes() is the only part that cares which one moved,
		// and it works that out from the product form itself.
		this.onProductVariantChange = () => {
			this.syncSizes();
			this.updateProductPrice();
		};

		/* Any cart change moves the bar — the PDP's own add to cart (the widget
		   only shows on a product in the collection), a row's own Add, and a
		   line changed or removed in the cart drawer. None of them announce it
		   in one shared way, so the cart endpoints' responses are watched
		   instead, the way CartCount watches /cart/add.js. The resource entry
		   is only recorded once the response is in, so the cart has moved by
		   the time it is re-read.

		   Which endpoint it was matters to the product's own price once the
		   product has been added: from then an add leaves it alone, and only a
		   change or update — the cart drawer stepping a line or removing it —
		   carries it (WEB-3256). Until then every one of them does (WEB-3267). */
		this.cartObserver = new PerformanceObserver(list => {
			const endpoints = list.getEntries()
				.filter(entry => entry.initiatorType === 'fetch')
				.map(entry => new URL(entry.name).pathname.match(/\/cart\/(add|change|update)\.js$/)?.[1])
				.filter(Boolean);

			if (endpoints.length) this.readCart({ shift: !this.productAdded || !endpoints.includes('add') });
		});

		/* A price test resolving rewrites both price lockups — PriceTest replaces
		   its parent's markup — and moves the figure variantPrice reads, so
		   everything quoting a price is composed again once growthbook answers.
		   Registered from connectedCallback rather than load(), since the product's
		   own price is re-priced before the rows are even asked for. */
		this.onPriceTest = () => this.updateProgress();
	}

	disconnectedCallback() {
		// A PDP colour swap replaces the whole section, and with it this element —
		// these would otherwise outlive it and keep driving the new section's add
		// to cart button from a detached widget's state.
		document.removeEventListener('variant:change', this.onProductVariantChange);
		window.removeEventListener('growthbookready', this.onPriceTest);
		window.removeEventListener('pdp:addingtocart', this.onProductAdd);
		this.cartObserver.disconnect();

		// The price lockups are not ours — they outlive this element (it removes
		// itself when a colour has nothing to upsell), so the tier comes back off.
		this.updateProductPrice(0);
	}

	connectedCallback() {
		if (this.loading) return;
		this.loading = true;

		// The size the page opened on, read before the shopper can have touched
		// it — syncSizes() carries a change away from this into the rows, and
		// leaves them alone while the selection is still what they'd default to
		// anyway (WEB-3204).
		this.syncedSizes = this.productSizes;

		// Before the rows are even asked for: the rungs are server-rendered, so
		// the prices around the widget can be at the tier the cart already puts
		// the shopper on while the placeholders hold the list's height under it.
		this.updateProgress();

		window.addEventListener('growthbookready', this.onPriceTest);

		// From connectedCallback rather than load(): the product can be added
		// before the rows are even asked for.
		window.addEventListener('pdp:addingtocart', this.onProductAdd);

		const observer = new IntersectionObserver(entries => {
			if (entries.some(entry => entry.isIntersecting)) {
				observer.disconnect();
				this.load();
			}
		}, { rootMargin: '200px' });

		observer.observe(this);
	}

	async load() {
		// Both in flight together — the cart count is a few bytes and shouldn't
		// hold the rows up, or wait for them
		const [resp] = await Promise.all([
			fetch(this.getAttribute('url')).catch(() => null),
			this.readCart({ rebase: true })
		]);

		if (!resp || !resp.ok) {
			this.remove();
			return;
		}

		const rows = this.querySelector('.buy-more__rows');

		// Held at nothing across the swap, so the placeholder bars don't fade out
		// on their way to being replaced — only the real rows' arrival animates
		rows.classList.add('buy-more__rows--entering');
		rows.innerHTML = await resp.text();

		if (!this.items.length) {
			this.remove();
			return;
		}

		this.initAdd();
		this.initAddAnother();
		this.initViewMore();

		// The rows arrive on their own products' default sizes. If the shopper
		// moved the page's size while they were in flight, that choice is the one
		// they're shopping in — so it goes on the rows too.
		this.syncSizes();

		// Next frame, so the browser has the empty state to transition from
		requestAnimationFrame(() => rows.classList.remove('buy-more__rows--entering'));

		document.addEventListener('variant:change', this.onProductVariantChange);
		this.cartObserver.observe({ type: 'resource' });

		this.updateProgress();
		this.classList.add('buy-more--loaded');
	}

	/* The size options the product form is currently on, as option key -> value
	   ("size" -> "Queen"). Read off the option-selectors rather than the select,
	   because that is where a row's own dropdown can be matched against: both
	   sides key their panels by the same handleized option name.

	   Sizes only. Colour is a different product on this site, not a variant, and
	   a row's colour is the shopper's to set per piece. */
	get productSizes() {
		const form = document.querySelector('.product-form > variant-selector');
		if (!form) return {};

		const sizes = {};

		for (const selector of form.querySelectorAll('option-selector')) {
			const key = selector.dataset.option;
			if (!key || !key.includes('size') || !selector.dataset.selected) continue;
			sizes[key] = selector.dataset.selected;
		}

		return sizes;
	}

	/* Put the page's size on every row that has it, once the shopper has moved
	   it off what the page opened on: a shopper buying a Queen duvet cover is
	   shopping for Queen sheets, so the rows shouldn't all be sitting on their
	   own defaults waiting to be corrected one by one (WEB-3204).

	   One-way, deliberately. A row's own size is about that piece — a King
	   pillowcase alongside a Queen sheet set is a real order — so changing one
	   never reaches back into the product form. This only ever reads it.

	   A row that doesn't carry the size at all, or carries it only as an
	   unorderable option, keeps the size it has: there is nothing to move it to.

	   Fires only on the step where the form's size actually moves, not on every
	   announcement — this runs on any variant change, a row's own included, and
	   re-applying each time would stamp the page's size straight back over a
	   size the shopper had just set on one row. Two sizes of one piece is the
	   whole point of "Add another", so a row's own choice has to survive
	   everything except the page's size moving again. */
	syncSizes() {
		// Nothing to carry it onto yet — the rows land well after this element
		// connects, and load() calls this again once they have. Bailing before
		// the record below is what leaves the change still pending for them.
		const items = this.items;
		if (!items.length) return;

		const sizes = this.productSizes;
		const moved = Object.entries(sizes).filter(([key, value]) => this.syncedSizes[key] !== value);
		if (!moved.length) return;

		for (const item of items) {
			for (const [key, value] of moved) {
				const selector = item.querySelector(`option-selector[data-option="${key}"]`);
				if (!selector || selector.dataset.selected === value) continue;

				const radio = [...selector.querySelectorAll('.option-selector__button input[type="radio"]')]
					.find(input => input.value === value && !input.disabled && input.dataset.soldout !== 'true');

				if (radio && !radio.checked) radio.click();
			}
		}

		this.syncedSizes = { ...this.syncedSizes, ...sizes };
	}

	/* How many of the collection's pieces the cart is holding. Read once as the
	   page loads, and again whenever the cart changes, since the bar fills with
	   what is actually in the cart (WEB-3220).

	   The load-time read is the page's own truth, so it moves the baseline the
	   prices are quoted against as well as the live count: that is what corrects
	   a cached page. A later read moves the live count — the bar and the rows —
	   and, when it's asked to (`shift`), the product's own price with it: every
	   move until the product has been added (WEB-3267), and after that only a
	   move made in the cart, a line stepped up, down or removed in the drawer,
	   so it never quotes a tier the cart has left (WEB-3256). It moves by what
	   the cart moved by rather than to it, so the product, once added, is still
	   not counted against itself, and a cart attribute written through
	   update.js moves nothing at all. */
	async readCart({ rebase = false, shift = false } = {}) {
		const url = this.getAttribute('cart-url');
		if (!url) return;

		// no-store: this is a GET on a collection URL whose answer depends on the
		// session's cart, so it must not come from the browser's cache
		const resp = await fetch(url, { cache: 'no-store' }).catch(() => null);
		if (!resp || !resp.ok) return;

		const pieces = parseInt(await resp.text());
		if (isNaN(pieces)) return;

		const rebased = rebase && pieces !== this.basePieces;
		if (!rebased && pieces === this.cartPieces) return;

		if (rebase) this.basePieces = pieces;
		else if (shift) this.basePieces = Math.max(0, this.basePieces + pieces - this.cartPieces);
		const before = this.discountAt(this.cartPieces);
		this.cartPieces = pieces;

		// A cart change that climbs the ladder. The load-time read only catches
		// up with a cart the shopper brought, so it reached nothing here.
		const tier = this.discountAt(pieces);
		if (!rebase && tier > before) this.track('Tier Reached', null, { tier });

		this.updateProgress();
	}

	/* The section's own wording for the row's Add button, when it carries one.
	   The template renders "Add" itself rather than waiting to be filled in the
	   way "Add another" is: a blank setting has to leave a working button behind,
	   since this is the only way a row can be bought (WEB-3220). */
	initAdd() {
		const label = this.dataset.addLabel;
		if (!label) return;

		for (const button of this.querySelectorAll('.buy-more__add')) button.textContent = label;
	}

	/* Fill in each row's "Add another <piece>" wording — the section setting
	   lives on this element, the row name on the button. */
	initAddAnother() {
		const template = this.dataset.addAnotherText;
		if (!template) return;

		for (const button of this.querySelectorAll('.buy-more__add-another')) {
			button.textContent = template.replace('$product', button.dataset.label);
			button.hidden = false;
		}
	}

	/* The list is clipped to the first --buy-more-visible-rows rows, with the
	   last of them faded to show there are more under it; the button drops the
	   clip. It has been holding its line since the first paint, so this either
	   reveals it or takes it away — nothing to reveal, or no clip set, and the
	   line goes with it.

	   The count is set per breakpoint — five rows on desktop, three on mobile
	   by default (WEB-3235) — so the clip is re-drawn whenever the viewport
	   crosses it, a tablet turned on its side included, until the shopper has
	   asked for the lot. */
	initViewMore() {
		const button = this.querySelector('.buy-more__view-more');
		if (!button) return;

		const rows = this.querySelector('.buy-more__rows');
		const items = this.items;
		const desktop = window.matchMedia('(min-width: 992px)');

		const clip = () => {
			const visible = parseInt(getComputedStyle(rows).getPropertyValue('--buy-more-visible-rows'));
			const clipped = visible > 0 && items.length > visible;

			items.forEach((item, i) => {
				item.classList.toggle('buy-more__item--clipped', clipped && i >= visible);
				item.classList.toggle('buy-more__item--last-visible', clipped && i === visible - 1);
			});

			button.style.display = clipped ? '' : 'none';
		};

		clip();
		button.classList.remove('buy-more__view-more--pending');
		desktop.addEventListener('change', clip);

		button.addEventListener('click', () => {
			desktop.removeEventListener('change', clip);
			this.track('View More Clicked', null, { rowCount: items.length });

			for (const item of items) {
				item.classList.remove('buy-more__item--clipped', 'buy-more__item--last-visible');
			}
			button.remove();
		});
	}

	/* A FullStory event about the widget (WEB-3246), carrying the PDP it's on
	   and, given a row, the piece that row offers — its type and the product it
	   is showing now, which a colour swap moves off the one it was built on. */
	track(name, item, properties = {}) {
		const option = item?.querySelector('select')?.selectedOptions[0];

		window.trackFSEvent?.(`BMSM ${name}`, {
			pdpProductType: this.getAttribute('product-type') || '',
			pdpHandle: this.getAttribute('handle') || '',
			...(item && {
				rowProductType: item.dataset.type || '',
				rowHandle: option?.dataset.productHandle || item.dataset.handle
			}),
			...properties
		});
	}

	/* The real rows. The loading placeholders carry .buy-more__item too — that's
	   how they hold a row's height — but they have no checkbox and take no part
	   in any of this, so everything that walks the list goes through here. */
	get items() {
		return [...this.querySelectorAll('.buy-more__item:not(.buy-more__item--placeholder)')];
	}

	quantityOf(item) {
		return parseInt(item.querySelector('[data-quantity]').textContent) || 1;
	}

	/* Fill the bar to what the cart holds and light up the highest rung it has
	   reached. The bar is measured in pieces actually in the cart (WEB-3220), not
	   in what is open, so a cart that arrived carrying pieces opens part-way along
	   it, and every further rung is reached by a piece going in.

	   The fill is continuous, not one step per rung: every piece added moves the
	   knob, interpolated between the rung it has passed and the one it is working
	   towards (see progressAt), so the bar answers the add even when the add
	   unlocks nothing.

	   A ticked row is only open, not added, so it doesn't move the bar.

	   The product's own price follows the fill until the product has been
	   added, then holds — only a change made in the cart moves it after that
	   (WEB-3256, WEB-3267). The rows' prices move on with the live count
	   (WEB-3234). */
	updateProgress() {
		const pieces = this.cartPieces;
		const rungs = [...this.querySelectorAll('.buy-more__tier-pieces')];

		// One piece is what a row's Add puts in the cart, and what the PDP's own
		// add to cart puts in — so both are quoted at the tier one more piece
		// reaches: the product from basePieces, the rows from the cart as it
		// stands. A row set to a quantity above one prices itself
		// higher up the ladder (see renderRow).
		this.discount = this.discountAt(this.basePieces + 1);
		this.rowDiscount = this.discountAt(this.cartPieces + 1);

		for (const item of this.items) this.renderRow(item);

		this.updateLabels();
		this.updateProductPrice();

		if (!rungs.length) return;

		const reached = rungs.filter(rung => pieces >= parseInt(rung.dataset.pieces));
		const current = reached[reached.length - 1];

		const progress = this.progressAt(pieces, rungs);

		this.style.setProperty('--buy-more-progress', `${progress}%`);
		this.classList.toggle('buy-more--tiered', progress > 0);

		for (const label of this.querySelectorAll('[data-pieces]')) {
			label.classList.toggle('is-active', !!current && label.dataset.pieces === current.dataset.pieces);
		}

		// Announced in the units the rungs are labelled in — pieces in the cart.
		const track = this.querySelector('.buy-more__track');
		track.setAttribute('aria-valuemin', 0);
		track.setAttribute('aria-valuemax', parseInt(rungs[rungs.length - 1].dataset.pieces));
		track.setAttribute('aria-valuenow', pieces);
		const count = `${pieces} piece${pieces === 1 ? '' : 's'} in your cart`;

		track.setAttribute('aria-valuetext', current
			? `${count} — ${this.discountFor(current)} off`
			: `${count} — no discount yet`);
	}

	/* Where the track's marker sits for the rung at `index` of `count`. Each one
	   is the centre of its column, which is where the design puts the labels — so
	   the knob lands under the rung it has just unlocked. The last rung is the
	   exception: its label stays centred like the rest, but unlocking it runs the
	   fill all the way out — "5+" has nothing past it to work towards. */
	markerAt(index, count) {
		return index >= count - 1 ? 100 : (index + 0.5) / count * 100;
	}

	/* How full the bar is at `added` pieces in the cart. Whole rungs land on
	   their own markers; anything between two of them is interpolated, so a
	   piece that unlocks nothing still moves the knob towards the rung it is
	   working towards (WEB-3220) — a ladder whose first rung is two pieces is
	   half way to it after one. */
	progressAt(added, rungs) {
		const counts = rungs.map(rung => parseInt(rung.dataset.pieces));
		const last = counts.length - 1;

		if (added >= counts[last]) return this.markerAt(last, counts.length);

		// The rung below where this lands, and the one it is heading for
		let below = -1;
		while (below < last && added >= counts[below + 1]) below++;

		const from = below < 0 ? 0 : this.markerAt(below, counts.length);
		const fromAt = below < 0 ? 0 : counts[below];
		const to = this.markerAt(below + 1, counts.length);
		const toAt = counts[below + 1];

		if (toAt <= fromAt) return to;

		return from + (to - from) * (added - fromAt) / (toAt - fromAt);
	}

	/* Percent off at a given piece count — the highest tier it reaches. Counted
	   in what the cart would end up holding, since that is what the discount is
	   paid on. */
	discountAt(pieces) {
		const reached = this.tiers.filter(tier => pieces >= tier.pieces);

		return reached.length ? reached[reached.length - 1].discount : 0;
	}

	/* A row's two prices, against the cart as it stands (WEB-3234): the
	   collapsed "From $x", quoted at the tier one more piece reaches, and the
	   opened row's own figure — its variant's price times its quantity, so it
	   matches the line its Add will put in the cart. A row set to two pieces
	   reaches further up the ladder than one does, so the opened figure is
	   priced at the tier its own quantity reaches.

	   A spent row's pieces are already in that count, so its figure is the tier
	   the cart is on — what the line it added is being charged now. */
	renderRow(item) {
		this.renderPrice(item.querySelector('.buy-more__price-from'), this.rowDiscount);

		const price = parseInt(item.dataset.price);
		if (isNaN(price)) return;

		const quantity = this.quantityOf(item);
		const compare = parseInt(item.dataset.comparePrice) || price;
		const discount = this.discountAt(this.cartPieces + (item.dataset.added ? 0 : quantity));

		const full = Math.max(compare, price) * quantity;
		const discounted = this.priceAfter(price, discount) * quantity;

		const el = item.querySelector('.buy-more__price-selected');
		el.innerHTML = discounted === full
			? money(discounted)
			: `<s>${money(full)}</s> <span>${money(discounted)}</span>`;
	}

	/* Rows added with "Add another" carry the same name as the one they came
	   from, so they're numbered — "Fitted Sheet", "Fitted Sheet +1" — to tell
	   them apart. data-handle is the row's identity and clones share it. */
	updateLabels() {
		const seen = new Map();

		for (const item of this.items) {
			const copies = seen.get(item.dataset.handle) || 0;
			seen.set(item.dataset.handle, copies + 1);
			item.querySelector('.buy-more__label-extra').textContent = copies ? ` +${copies}` : '';
		}
	}

	/* One price at one tier, rounded per unit — the same arithmetic a cart line
	   discount does, so the total is the sum of what's on screen. */
	priceAfter(price, discount) {
		return discount ? Math.round(price * (100 - discount) / 100) : price;
	}

	/* The collapsed "From" figure, re-priced against the tier that applies to
	   the row. data-price is the price after any discount the row already has
	   (a sale, clearance) and data-compare-price the figure before it — the same
	   pair the price snippets strike through. data-no-strike shows the
	   discounted figure on its own.

	   With no tier reached the server's own markup goes back in untouched,
	   which keeps price tests and currency formatting exactly as rendered. */
	renderPrice(el, discount) {
		if (el.rendered === undefined) el.rendered = el.innerHTML;

		if (!discount) {
			el.innerHTML = el.rendered;
			return;
		}

		const price = parseInt(el.dataset.price);
		if (isNaN(price)) return;

		const compare = parseInt(el.dataset.comparePrice) || price;
		const format = el.hasAttribute('data-money-short') ? moneyShort : money;
		const struck = el.hasAttribute('data-no-strike') ? '' : `<s>${format(Math.max(compare, price))}</s> `;

		el.innerHTML = `${el.dataset.prefix || ''}${struck}<span>${format(this.priceAfter(price, discount))}</span>`;
	}

	discountFor(tier) {
		const discount = this.querySelector(`.buy-more__tier-discount[data-pieces="${tier.dataset.pieces}"]`);
		return discount ? discount.textContent.trim().replace(/\s*Off$/i, '') : '';
	}

	/* The PDP's own selected option, read off the product form. The widget only
	   reads that form — it never writes to it. */
	get productOption() {
		return document.querySelector('.product-form > variant-selector select')?.selectedOptions[0];
	}

	/* Everywhere the PDP quotes this product's own price, and which of the two
	   price snippets drew it:

	   - under the title and in the sticky bar, both full variant-price-text
	     lockups;
	   - the size dropdown's two variant-price-text-short renders — the trigger's
	     "Double - $130" and the price/stock row above it, one shown per
	     breakpoint. That short form is the same <s>…</s> <span>…</span> pair the
	     rows draw, so priceMarkup composes it.

	   Scoped to `.product-form > variant-selector` so the rows' own selectors,
	   which price themselves, are left alone.

	   The target is always the span *inside* the wrapper, never the wrapper
	   itself: for a price-tested product the wrapper also holds the <price-test>
	   elements, and PriceTest replaces its own parentElement's markup — detaching
	   them would throw the next time growthbook answered.

	   Re-resolved on every render rather than held, because product-page.js,
	   StickyAddToCart.updatePrice and VariantSelector.updateSelected all rewrite
	   their wrapper on a variant change, so the span is a different element by
	   the time we run. We always run after them: those three are on the
	   selector's `variantchange`, this is on the document-level `variant:change`
	   that follows it. */
	get productPriceTargets() {
		const lockups = [
			{ el: document.querySelector('.product__price-text > span'), main: true },
			{ el: document.querySelector('sticky-add-to-cart .sticky-atc__price > span'), main: false }
		];

		const short = [...document.querySelectorAll('.product-form > variant-selector .variant-selector__price > span')];

		return [
			...lockups.filter(target => target.el).map(target => ({ ...target, short: false })),
			...short.map(el => ({ el, short: true, main: false }))
		];
	}

	/* Re-price this product against the tier its own add to cart would reach,
	   everywhere the page quotes it — under the title, in the size dropdown and
	   in the sticky bar. It is a piece of the discount's collection like any row,
	   so adding it lands it on whatever tier the cart then holds, and that is the
	   price the page should be quoting for it. It moves on as rows go in
	   (WEB-3267) until it's been added itself, then holds still so it isn't
	   offered a discount for being added again (WEB-3223) — moving only with
	   whatever the shopper changes in the cart itself (WEB-3256).

	   Stacking is the rows' rule: the tier comes off the price after any discount
	   the variant already carries, and whichever of that and the compare price is
	   higher is the one struck. So a full-price Queen duvet cover strikes its own
	   $250, and one already on sale keeps its original struck with the tier coming
	   off the sale price.

	   A size on clearance (Last Chance, "we made too many") takes no tier on top
	   of its clearance price — that stacking isn't offered (WEB-3252) — so it
	   keeps the page's own lockup, as though no tier were reached.

	   With no tier reached the page's own lockup goes back in verbatim, which is
	   what keeps price tests, currency formatting and the `zeroes` spans exactly
	   as they were rendered. */
	updateProductPrice(discount = this.discount) {
		const option = this.productOption;
		if (option?.dataset.clearance) discount = 0;

		const price = option ? variantPrice(option.dataset) : NaN;
		const compare = parseInt(option?.dataset.comparePrice) || price;

		const full = Math.max(compare, price);
		const discounted = this.priceAfter(price, discount);

		for (const { el, short, main } of this.productPriceTargets) {
			// Anything we didn't write ourselves is the page's own markup, freshly
			// put there by a variant change or a resolved price test — so it becomes
			// what dropping back below the first tier returns to.
			if (el.innerHTML !== el.buyMorePriced) el.buyMoreBase = el.innerHTML;

			let html = el.buyMoreBase;

			if (discount && price > 0) {
				html = short
					? this.priceMarkup({ full, discounted })
					: this.discountedPriceMarkup(full, discounted, main);
			}

			// Only when it actually moves: this runs on every tick, quantity step
			// and variant change, and rewriting the lockup with itself would tear
			// down and rebuild any <price-test> sitting in it each time.
			if (html !== el.innerHTML) el.innerHTML = html;

			el.buyMorePriced = el.innerHTML;
		}
	}

	/* The same lockup variant-price-text draws for a variant that's on sale —
	   struck original, discounted figure, percentage off — so the tier price is
	   picked up by the styling already on the page, in the sticky bar as well as
	   under the title. The discounted figure is formatted the way that snippet
	   formats it (money_without_trailing_zeros), the struck one the way it formats
	   that, and the percentage is floored the way variant-discount floors it, so
	   nothing here reads higher than what is actually coming off. */
	discountedPriceMarkup(full, discounted, main = false) {
		const percent = Math.floor((full - discounted) / full * 100);

		// Under the title the percentage and what it's conditional on are one
		// sentence — "15% off when you add this to your order" — in the one span,
		// so they can't be told apart as a tag with a clause hung off it
		// (WEB-3256). Everywhere else it's the tag on its own, cased like the
		// rest of the site's.
		const discount = main ? `${percent}% off ${TIER_PRICE_SUFFIX}` : `${percent}% Off`;

		// Joined on whitespace, the way the snippet's own newlines separate them:
		// the spans are inline and the only thing between the struck figure and
		// the one beside it is a zero-width visually-hidden label, so without it
		// the two prices run together.
		return [
			'<span class="visually-hidden">Original price:</span>',
			`<span class="product__price--original"><s>${money(full)}</s></span>`,
			'<span class="visually-hidden">Discounted price:</span>',
			`<span class="product__price--discounted product__price--sale">${moneyShort(discounted)}</span>`,
			`<span class="product__discount-amount"><span class="variant-discount">${discount}</span></span>`
		].join('\n');
	}

	/* One price, two figures — struck original and what it comes to — in the
	   shape the size dropdown's short lockup draws it. */
	priceMarkup({ full, discounted }) {
		return discounted === full
			? money(full)
			: `<s>${money(full)}</s> <span>${money(discounted)}</span>`;
	}

	/* One row as a cart line, in the shape addAllToCart wants. Read straight off
	   the row's own selector, so whatever is on screen is what gets bought. */
	lineFor(item) {
		const option = item.querySelector('select')?.selectedOptions[0];
		if (!option) return null;

		const handle = option.dataset.productHandle;

		// The same two property sets the PDP's own line gets, so a row bought
		// through here is indistinguishable from the same product bought on its
		// own page.
		const properties = { ...Object.entries(window.theme.customProps).find(([key]) => handle.includes(key))?.[1] };
		if (option.dataset.priceTest === 'true' && window.getActivePriceTest()) {
			properties['_PriceTest'] = window.getActivePriceTest();
		}

		return {
			handle,
			variantId: option.value,
			quantity: this.quantityOf(item),
			isBundle: option.dataset.isBundle === 'true',
			preorderMonth: option.dataset.status === 'preorder' ? option.dataset.preorderMonth : null,
			properties
		};
	}

	/* A row buys itself (WEB-3220): its Add puts that one piece in the cart
	   straight away and on its own, and nothing else on the page goes in with it.

	   The cart drawer is refreshed but deliberately not opened. The shopper is
	   part-way down a list of pieces, and throwing them into the cart after each
	   one would be the end of that. The "Added to cart" signifier says the piece
	   went in instead, for that piece, on every add and at every width (WEB-3244,
	   WEB-3255) — the bar moving and the button going spent were too quiet to
	   say it on their own. The header count looks after itself: CartCount
	   watches for /cart/add.js.

	   The row is left exactly as it stands: still open, on the size and colour
	   that went in, with its button disabled and reading "Added!" for
	   ADDED_DURATION, after which it's put back (see resetAdd) to offer another.
	   That includes a row "Add another" put there. Moving the row onto another
	   colour or size puts the button back sooner — it is offering a different
	   piece by then — and so does closing it (see onToggle).

	   The cart is then re-read, which is what moves the bar. */
	async addItem(button) {
		if (button.disabled) return;

		const item = button.closest('.buy-more__item');
		const line = this.lineFor(item);
		if (!line) return;

		// Read before the add, while the row still shows what's going in
		const sku = item.querySelector('select')?.selectedOptions[0]?.dataset.sku || '';

		const error = item.querySelector('.buy-more__error');
		error.textContent = '';

		button.disabled = true;
		button.classList.add('show-loading');

		const message = await addAllToCart([line]).catch(() => 'Something went wrong. Please try again.');

		button.classList.remove('show-loading');

		if (message) {
			error.textContent = message;
			button.disabled = false;
			return;
		}

		window.updateCartDrawer?.();
		window.announceCartUpdate?.(`${item.querySelector('.buy-more__label').textContent.trim()} added to cart`);

		this.showSignifier(item);

		// The row's own selectors stay live while the line is in flight, so the
		// shopper can have moved it off what actually went in. The piece is in the
		// cart either way — only the button's reading is in question — and a row
		// now showing another colour is offering that colour, not reporting the
		// one before it. So it comes straight back rather than sitting on
		// "Added!" for a variant that never was.
		if (item.querySelector('select')?.selectedOptions[0]?.value !== line.variantId) {
			button.disabled = false;
		} else {
			// Marks the row as spent, so resetAdd knows to put its button back
			item.dataset.added = 'true';
			button.textContent = 'Added!';

			clearTimeout(item.addedTimer);
			item.addedTimer = setTimeout(() => {
				this.resetAdd(item);
				// Offering another now, so its price counts it again
				this.renderRow(item);
			}, ADDED_DURATION);
		}

		this.track('Added To Cart', item, {
			sku,
			quantity: line.quantity,
			preorder: !!line.preorderMonth
		});

		await this.readCart({ shift: !this.productAdded });
		this.updateProgress();
	}

	/* The PDP's "Added to cart" signifier (snippets/cart-signifier.liquid), shown
	   for the piece a row just added rather than the product being viewed.

	   The signifier is the page's, rendered with this product's title and image,
	   so this swaps the row's in for as long as it's up. The page's are put back
	   the next time anything else shows it, which is what the wrapper on
	   activateSignifier does — the PDP's own add to cart goes through it and has
	   no idea anything here changed. Wrapped lazily, on the first add, because
	   the signifier's script lands after this one. */
	showSignifier(item) {
		const signifier = document.querySelector('.cart-signifier');
		const title = signifier?.querySelector('.cart-signifier__title');
		const image = signifier?.querySelector('.cart-signifier__image img');
		const option = item.querySelector('select')?.selectedOptions[0];
		if (!title || !image || !option || !window.activateSignifier) return;

		if (!signifier.buyMoreShow) {
			const page = { title: title.textContent, src: image.src };
			const show = window.activateSignifier;

			window.activateSignifier = (...args) => {
				title.textContent = page.title;
				image.src = page.src;
				return show(...args);
			};

			signifier.buyMoreShow = show;
		}

		title.textContent = option.dataset.productTitle || item.querySelector('.buy-more__label').textContent.trim();
		image.src = item.querySelector('.buy-more__image').src;

		signifier.buyMoreShow();
	}

	/* A spent row keeps its "Added!" for ADDED_DURATION, or until the shopper
	   asks it for something else, or closes it. Another colour or another size
	   is a different piece from the one that went in, so the button comes back
	   for it rather than leaving the row a dead end (the piece it added stays in
	   the cart, and the bar with it).

	   Rows still mid-add are never spent — `added` is only set once the line has
	   landed — so this leaves one alone until it is. */
	resetAdd(item) {
		if (!item.dataset.added) return;

		clearTimeout(item.addedTimer);
		delete item.dataset.added;

		const button = item.querySelector('.buy-more__add');
		button.textContent = this.dataset.addLabel || 'Add';
		button.disabled = false;

		item.querySelector('.buy-more__error').textContent = '';
	}

	onVariantChange(e) {
		const item = e.target.closest('.buy-more__item');
		if (!item) return;

		const variant = e.detail.variant;

		// A colour swap puts another product in the row; a size change doesn't.
		// Kept as an attribute so an "Add another" copy starts on its row's.
		const shown = item.dataset.shownHandle || item.dataset.handle;
		if (variant.productHandle && variant.productHandle !== shown) {
			item.dataset.shownHandle = variant.productHandle;
			this.track('Colour Changed', item, { colour: variant.optionColour || variant.optionColor || '' });
		}

		item.dataset.price = variantPrice(variant);
		item.dataset.comparePrice = variant.comparePrice;

		// The product image, not the variant's — a size change shouldn't swap it,
		// but picking another colour swaps the row over to that colour's product
		if (variant.productImage) {
			item.querySelector('.buy-more__image').src = variant.productImage;
		}

		// The row is offering a different piece now, so a spent Add is put back.
		// Fires for a size carried in from the product form too (syncSizes), which
		// is the same thing happening for the same reason.
		this.resetAdd(item);

		this.updateProgress();
	}

	onClick(e) {
		const step = e.target.closest('[data-quantity-step]');
		if (step) return this.onQuantityStep(step);

		const add = e.target.closest('.buy-more__add');
		if (add) return this.addItem(add);

		const addAnother = e.target.closest('.buy-more__add-another');
		if (addAnother) return this.addAnother(addAnother.closest('.buy-more__item'));
	}

	/* Closing a spent row is done with the piece it added, so it is put back to
	   rest: opened again, it offers another one, at the price its collapsed
	   "From" was quoting (WEB-3253) — not the line already in the cart. */
	onToggle(e) {
		if (!e.target.matches('.buy-more__checkbox')) return;

		const item = e.target.closest('.buy-more__item');

		if (e.target.checked) {
			this.track('Checkbox Opened', item, { openedOnPage: ++this.openedCount });
			return;
		}

		// Whether the shopper is closing a row whose piece they've just added
		// (WEB-3233's question: do they expect closing to take it back out?)
		this.track('Checkbox Closed', item, { alreadyInCart: !!item.dataset.added });

		if (!item.dataset.added) return;

		this.resetAdd(item);
		this.renderRow(item);
	}

	onQuantityStep(button) {
		const item = button.closest('.buy-more__item');
		const value = item.querySelector('[data-quantity]');
		const quantity = Math.max(1, parseInt(value.textContent) + parseInt(button.dataset.quantityStep));

		value.textContent = quantity;
		item.querySelector('[data-quantity-step="-1"]').disabled = quantity <= 1;

		this.track('Quantity Changed', item, { quantity, step: parseInt(button.dataset.quantityStep) });

		this.updateProgress();
	}

	/* A second (third, ...) of the same piece, at its own size and colour — a
	   whole row of its own, so it can be set and unticked independently of the
	   one it came from. It goes directly under the last row of its group, so
	   copies of a piece stay together. Only pillowcases carry the link that asks
	   for one (WEB-3221).

	   It arrives unticked, like any other row in the list: asking for a second
	   one is asking for the row, not for the piece — the shopper ticks it, sets
	   it and adds it, the same three gestures every other row takes (WEB-3198).

	   cloneNode copies whatever state the row it came from is in — its
	   checkbox's checkedness included — and the row it came from is very often
	   one that has just been added, so the copy's checkbox is cleared and its own
	   Add is put back to rest, and the spent marker with it.

	   Its selection starts where the row it came from is: the incoming
	   variant-selector re-derives its select from the option-selectors'
	   data-selected attributes when it connects. */
	addAnother(item) {
		this.track('Add Another Clicked', item);

		const group = [...this.querySelectorAll(`.buy-more__item[data-handle="${item.dataset.handle}"]`)];
		const clone = uniquify(item.cloneNode(true));

		delete clone.dataset.added;
		clone.querySelector('.buy-more__checkbox').checked = false;
		clone.querySelector('[data-quantity]').textContent = 1;
		clone.querySelector('[data-quantity-step="-1"]').disabled = true;

		const add = clone.querySelector('.buy-more__add');
		add.textContent = this.dataset.addLabel || 'Add';
		add.disabled = false;
		add.classList.remove('show-loading');

		clone.querySelector('.buy-more__error').textContent = '';

		// Whatever the list was clipped to, a row the shopper just asked for is
		// not part of it
		clone.classList.remove('buy-more__item--clipped', 'buy-more__item--last-visible');

		group[group.length - 1].after(clone);

		this.updateProgress();
	}
}

customElements.define('buy-more-save-more', BuyMoreSaveMore);
